import React, { useEffect, useState } from "react";
import axios from "axios";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

import "./AtplMensEventReport.css";
import { FiDownload, FiEye } from "react-icons/fi";

export default function AtplMensEventReport() {
  const API = "https://node.aattumtpl.com/api/admin/reports/atpl-mens-report";

  const [data, setData] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [teamNames, setTeamNames] = useState([]);
  const [cities, setCities] = useState([]);

  const [type, setType] = useState("All");
  const [teamName, setTeamName] = useState("");
  const [city, setCity] = useState("");
  const [userIdFilter, setUserIdFilter] = useState("");
  const [search, setSearch] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const [popupData, setPopupData] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const res = await axios.get(API);

      // Flatten address fields
      const formatted = res.data.data.map((item) => ({
        ...item,
        street: item.address?.street || "",
        city: item.address?.city || "",
        state: item.address?.state || "",
        postalCode: item.address?.postalCode || "",
      }));

      setData(formatted);
      setFiltered(formatted);
      setTeamNames(res.data.filters.teamNames);
      setCities(res.data.filters.cities);
    } catch (error) {
      console.error(error);
    }
  };

  // Apply Filters
  useEffect(() => {
    let temp = [...data];

    if (type !== "All") temp = temp.filter((item) => item.type === type);
    if (teamName) temp = temp.filter((item) => item.teamName === teamName);
    if (city) temp = temp.filter((item) => item.city === city);
    if (userIdFilter.trim())
      temp = temp.filter((item) =>
        item.userId?.toLowerCase().includes(userIdFilter.toLowerCase())
      );
    if (search.trim())
      temp = temp.filter((item) =>
        JSON.stringify(item).toLowerCase().includes(search.toLowerCase())
      );
    if (startDate)
      temp = temp.filter(
        (item) => new Date(item.createdAt) >= new Date(startDate)
      );
    if (endDate)
      temp = temp.filter(
        (item) => new Date(item.createdAt) <= new Date(endDate)
      );

    setFiltered(temp);
  }, [type, teamName, city, userIdFilter, search, startDate, endDate, data]);

  // Prepare export data
  const prepareExportData = (rows) => {
    return rows.map((row) => {
      let obj = { ...row };

      row.players?.forEach((p, i) => {
        obj[`Player${i + 1}`] = `${p.name} | ${p.age} | ${p.position} | ${p.arm}`;
      });

      row.substitutes?.forEach((s, i) => {
        obj[`Substitute${i + 1}`] = `${s.name} | ${s.age} | ${s.position} | ${s.arm}`;
      });

      delete obj.players;
      delete obj.substitutes;
      delete obj.address;

      return obj;
    });
  };

  // Export Excel
  const exportExcel = () => {
    const exportData = prepareExportData(filtered);
    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Report");
    XLSX.writeFile(wb, "ATPL_Mens_Event_Report.xlsx");
  };

  // Export CSV
  const exportCSV = () => {
    const exportData = prepareExportData(filtered);
    const ws = XLSX.utils.json_to_sheet(exportData);
    const csv = XLSX.utils.sheet_to_csv(ws);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    saveAs(blob, "ATPL_Mens_Event_Report.csv");
  };

  // Export PDF
  const exportPDF = () => {
    const doc = new jsPDF("l", "pt", "a4");
    doc.setFontSize(18);
    doc.text("ATPL Men's Event Report", 40, 40);

    const tableColumns = [
      "Type",
      "User ID",
      "Name/Team Name",
      "City",
      "State",
      "Phone",
      "Email",
      "Players / Individual Info",
    ];

    const tableRows = filtered.map((row) => {
      const playersList = row.players
        ?.map((p) => `• ${p.name} (${p.position}, ${p.arm})`)
        .join("\n");

      const subsList = row.substitutes
        ?.map((s) => `- ${s.name} (${s.position}, ${s.arm})`)
        .join("\n");

      const individualDetails =
        row.type === "Individual"
          ? `Height: ${row.height} cm\nWeight: ${row.weight} kg\nPosition: ${row.position}\nArm: ${row.arm}`
          : "";

      const finalBlock =
        row.type === "Team"
          ? `${playersList}\n\nSubstitutes:\n${subsList}`
          : individualDetails;

      return [
        row.type,
        row.userId,
        row.type === "Team" ? row.teamName : row.name,
        row.city,
        row.state,
        row.phone,
        row.email,
        finalBlock,
      ];
    });

    autoTable(doc, {
      startY: 60,
      head: [tableColumns],
      body: tableRows,
      styles: { fontSize: 10, overflow: "linebreak" },
      columnStyles: {
        7: { cellWidth: 250 },
      },
    });

    doc.save("ATPL_Mens_Event_Report.pdf");
  };

  return (
    <div className="KK-admin-container">
      <h1 className="KK-admin-title">ATPL Men's T20 Event Report</h1>

      {/* Buttons */}
      <div className="KK-download-buttons">
        <button className="KK-btn" onClick={exportExcel}>Excel</button>
        <button className="KK-btn" onClick={exportCSV}>CSV</button>
        <button className="KK-btn" onClick={exportPDF}>PDF</button>
      </div>

      {/* Filters */}
      <div className="KK-filter-box">
        <select className="KK-select" onChange={(e) => setType(e.target.value)}>
          <option value="All">All</option>
          <option value="Team">Team</option>
          <option value="Individual">Individual</option>
        </select>

        <select className="KK-select" onChange={(e) => setTeamName(e.target.value)}>
          <option value="">Team Name</option>
          {teamNames.map((n) => (
            <option key={n}>{n}</option>
          ))}
        </select>

        <select className="KK-select" onChange={(e) => setCity(e.target.value)}>
          <option value="">City</option>
          {cities.map((c) => (
            <option key={c}>{c}</option>
          ))}
        </select>

        <input
          type="text"
          placeholder="User ID"
          className="KK-input"
          onChange={(e) => setUserIdFilter(e.target.value)}
        />

        <input
          type="text"
          placeholder="Search"
          className="KK-input"
          onChange={(e) => setSearch(e.target.value)}
        />

        <input type="date" className="KK-input" onChange={(e) => setStartDate(e.target.value)} />
        <input type="date" className="KK-input" onChange={(e) => setEndDate(e.target.value)} />
      </div>

      {/* Table */}
      <div className="KK-table-wrapper">
        <div className="KK-table-scroll">
          <table className="KK-styled-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Type</th>
                <th>User ID</th>
                <th>Name / Team Name</th>
                <th>City</th>
                <th>State</th>
                <th>Phone</th>
                <th>Email</th>
                <th>Created At</th>
                <th>ACTION</th>
              </tr>
            </thead>

            <tbody>
              {filtered.length > 0 ? (
                filtered.map((row, idx) => (
                  <tr key={idx}>
                    <td>{row.id}</td>
                    <td>{row.type}</td>
                    <td>{row.userId}</td>
                    <td>{row.type === "Team" ? row.teamName : row.name}</td>
                    <td>{row.city}</td>
                    <td>{row.state}</td>
                    <td>{row.phone}</td>
                    <td>{row.email}</td>
                    <td>{new Date(row.createdAt).toLocaleDateString()}</td>

                    <td className="action-buttons">
                      <button
                        className="KK-icon-btn"
                        onClick={() => exportExcel([row], idx)}
                      >
                        <FiDownload className="KK-icon" />
                      </button>

                      <button
                        className="KK-icon-btn view"
                        onClick={() => setPopupData(row)}
                      >
                        <FiEye className="KK-icon" />
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="10" className="KK-no-results">
                    No results found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* POPUP MODAL */}
      {popupData && (
        <div className="KK-popup-overlay" onClick={() => setPopupData(null)}>
          <div className="KK-popup-content" onClick={(e) => e.stopPropagation()}>
            <div className="KK-popup-header">
              <h2>
                {popupData.type === "Team"
                  ? "Team Details"
                  : "Individual Details"}
              </h2>
              <button className="close-btn" onClick={() => setPopupData(null)}>
                ×
              </button>
            </div>

            <div className="KK-popup-body">
              {popupData.type === "Team" ? (
                <>
                  {/* TEAM DETAILS */}
                  <div className="KK-popup-card">
                    <h3>Team Info</h3>

                    <p><strong>Team Name:</strong> {popupData.teamName}</p>
                    <p><strong>Captain:</strong> {popupData.captainFirstName} {popupData.captainLastName}</p>
                    <p><strong>Phone:</strong> {popupData.phone}</p>
                    <p><strong>Email:</strong> {popupData.email}</p>

                    <h4>Address</h4>
                    <p><strong>Street:</strong> {popupData.street}</p>
                    <p><strong>City:</strong> {popupData.city}</p>
                    <p><strong>State:</strong> {popupData.state}</p>
                    <p><strong>Postal Code:</strong> {popupData.postalCode}</p>
                  </div>

                  {/* Players */}
                  <div className="KK-popup-card">
                    <h3>Main Players</h3>
                    <ul className="KK-player-list">
                      {popupData.players?.map((p, i) => (
                        <li key={i}>
                          {p.no}. {p.name} | Age: {p.age} | {p.position} | {p.arm}
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Substitutes */}
                  <div className="KK-popup-card">
                    <h3>Substitutes</h3>
                    <ul className="KK-player-list">
                      {popupData.substitutes?.map((s, i) => (
                        <li key={i}>
                          {s.no}. {s.name} | Age: {s.age} | {s.position} | {s.arm}
                        </li>
                      ))}
                    </ul>
                  </div>
                </>
              ) : (
                <div className="KK-popup-card">
                  <h3>Individual Details</h3>

                  <p><strong>Name:</strong> {popupData.name}</p>
                  <p><strong>Phone:</strong> {popupData.phone}</p>
                  <p><strong>Email:</strong> {popupData.email}</p>

                  <h4>Physical</h4>
                  <p><strong>Height:</strong> {popupData.height} cm</p>
                  <p><strong>Weight:</strong> {popupData.weight} kg</p>

                  <h4>Cricket Info</h4>
                  <p><strong>Position:</strong> {popupData.position}</p>
                  <p><strong>Arm:</strong> {popupData.arm}</p>

                  <h4>Address</h4>
                  <p><strong>Street:</strong> {popupData.street}</p>
                  <p><strong>City:</strong> {popupData.city}</p>
                  <p><strong>State:</strong> {popupData.state}</p>
                  <p><strong>Postal Code:</strong> {popupData.postalCode}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
